import { Injectable } from '@angular/core';
import { Router, Event, NavigationStart, NavigationEnd, NavigationCancel, NavigationError, RouteConfigLoadStart, RouteConfigLoadEnd, GuardsCheckStart, GuardsCheckEnd, ResolveStart, ResolveEnd, RoutesRecognized } from '@angular/router';

type NavMarks = {
  id: number;
  url: string;
  start: number;
  lazyStart?: number;
  lazyEnd?: number;
  guardsStart?: number;
  guardsEnd?: number;
  resolveStart?: number;
  resolveEnd?: number;
  recognizedAt?: number;
};

@Injectable({ providedIn: 'root' })
export class NavigationProfilerService {
  private enabled = false;
  private logEnabled = false;
  private current: NavMarks | null = null;
  private navSeq = 0;

  constructor(private router: Router) {
    // Activar solo en Safari o si debug está habilitado para evitar ruido y costo extra
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua);
  const debugFlag = (typeof location !== 'undefined' && /[?&]debug=1/.test(location.search)) || (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG') === '1');
  this.enabled = isSafari || debugFlag; // medir en Safari
  this.logEnabled = !!debugFlag;        // pero solo log si debug explícito

    if (!this.enabled) return;

    this.router.events.subscribe((event: Event) => {
      this.onRouterEvent(event);
    });
  }

  private now(): number {
    try { return performance.now(); } catch { return Date.now(); }
  }

  private onRouterEvent(event: Event) {
    if (event instanceof NavigationStart) {
      this.current = {
        id: ++this.navSeq,
        url: event.url,
        start: this.now(),
      };
      // Marcas de Performance para timeline
      this.safeMark(`nav:${this.current.id}:start`);
      return;
    }

    if (!this.current) return;

    if (event instanceof RoutesRecognized) {
      this.current.recognizedAt = this.now();
      this.safeMark(`nav:${this.current.id}:recognized`);
    } else if (event instanceof RouteConfigLoadStart) {
      this.current.lazyStart = this.now();
      this.safeMark(`nav:${this.current.id}:lazyStart`);
    } else if (event instanceof RouteConfigLoadEnd) {
      this.current.lazyEnd = this.now();
      this.safeMark(`nav:${this.current.id}:lazyEnd`);
      this.safeMeasure(`nav:${this.current.id}:lazy`, `nav:${this.current.id}:lazyStart`, `nav:${this.current.id}:lazyEnd`);
    } else if (event instanceof GuardsCheckStart) {
      this.current.guardsStart = this.now();
      this.safeMark(`nav:${this.current.id}:guardsStart`);
    } else if (event instanceof GuardsCheckEnd) {
      this.current.guardsEnd = this.now();
      this.safeMark(`nav:${this.current.id}:guardsEnd`);
      this.safeMeasure(`nav:${this.current.id}:guards`, `nav:${this.current.id}:guardsStart`, `nav:${this.current.id}:guardsEnd`);
    } else if (event instanceof ResolveStart) {
      this.current.resolveStart = this.now();
      this.safeMark(`nav:${this.current.id}:resolveStart`);
    } else if (event instanceof ResolveEnd) {
      this.current.resolveEnd = this.now();
      this.safeMark(`nav:${this.current.id}:resolveEnd`);
      this.safeMeasure(`nav:${this.current.id}:resolve`, `nav:${this.current.id}:resolveStart`, `nav:${this.current.id}:resolveEnd`);
    } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
      const end = this.now();
      const { start, lazyStart, lazyEnd, guardsStart, guardsEnd, resolveStart, resolveEnd, recognizedAt, url, id } = this.current;
      const total = (end - start).toFixed(1);
      const lazy = lazyStart && lazyEnd ? (lazyEnd - lazyStart).toFixed(1) : '-';
      const guards = guardsStart && guardsEnd ? (guardsEnd - guardsStart).toFixed(1) : '-';
      const resolve = resolveStart && resolveEnd ? (resolveEnd - resolveStart).toFixed(1) : '-';
      const recognize = recognizedAt ? (recognizedAt - start).toFixed(1) : '-';

      this.safeMark(`nav:${id}:end`);
      this.safeMeasure(`nav:${id}:total`, `nav:${id}:start`, `nav:${id}:end`);

      // Resumen compacto en consola
      const status = event instanceof NavigationEnd ? 'END' : event instanceof NavigationCancel ? 'CANCEL' : 'ERROR';
      // Post-log para no interferir en la navegación (usa polyfill si no existe requestIdleCallback)
      if (this.logEnabled) {
        this.scheduleIdle(() => {
          // eslint-disable-next-line no-console
          console.log(`🧭 [NAV ${status}] ${url} total=${total}ms recognize=${recognize}ms lazy=${lazy}ms guards=${guards}ms resolve=${resolve}ms`);
        });
      }

      // Guardar último resultado para inspección manual (window.__navPerf)
      try {
        const w = window as any;
  w.__navPerf = w.__navPerf || [];
  w.__navPerf.push({ id, url, total: Number(total), recognize, lazy, guards, resolve, t0: start, t1: end });
  if (w.__navPerf.length > 20) w.__navPerf.shift();
      } catch {}

      this.current = null;
    }
  }

  private safeMark(name: string) {
    try { performance.mark(name); } catch {}
  }
  private safeMeasure(name: string, start: string, end: string) {
    try { performance.measure(name, start, end); } catch {}
  }

  private scheduleIdle(cb: () => void) {
    try {
      const w: any = typeof window !== 'undefined' ? window : undefined;
      const ric = w && typeof w.requestIdleCallback === 'function' ? w.requestIdleCallback : null;
      if (ric) {
        ric(() => cb(), { timeout: 500 });
      } else {
        setTimeout(cb, 0);
      }
    } catch {
      setTimeout(cb, 0);
    }
  }
}
